/**
 * 六维分类（G-1）的**界面定义**。
 *
 * 这份数据不是 mock：它是分类向导的展示层契约（标题、提问、帮助文案与选项标签），
 * `value` 取值与后端方法包 `GET /methodologies/current` 的 `enums` 一一对应，
 * 与 `packages/methodology/schemas/project_profile.schema.json` 同源。
 * 任一侧新增取值都必须同步另一侧（由 `make verify` 的契约测试守护）。
 *
 * 之所以从这里而不是 `api/mock.ts` 导出：mock 模块是**离线替身**，
 * 只能被 `api/queries.ts` 的显式离线模式引用（RULES §11.2）；
 * 分类枚举是真实产物，必须在生产路径可用。
 */

export type DimensionId =
  | "product_form"
  | "interaction_mode"
  | "dramatic_genre"
  | "subject_domain"
  | "audience_profile"
  | "platform_profile";

export interface DimensionOption {
  value: string;
  label: string;
  hint: string;
}

export interface DimensionDef {
  id: DimensionId;
  index: number;
  title: string;
  question: string;
  help: string;
  options: DimensionOption[];
}

export const TAXONOMY: DimensionDef[] = [
  {
    id: "product_form",
    index: 1,
    title: "产品形态",
    question: "这个项目最终以什么形态交付给观众？",
    help: "决定主干粒度与素材复用策略，是唯一不可由项目级覆盖的维度。",
    options: [
      {
        value: "INTERACTIVE_FILM_GAME",
        label: "互动影游",
        hint: "玩家化身主角，选择改变状态与后续成本",
      },
      { value: "INTERACTIVE_DRAMA", label: "互动剧", hint: "以观看为主，选择改变关系与结果" },
      { value: "HYBRID", label: "混合形态", hint: "观看与操作交替，需额外说明触发条件" },
    ],
  },
  {
    id: "interaction_mode",
    index: 2,
    title: "交互模式",
    question: "玩家主要靠什么能力推进故事？",
    help: "可另选至多 2 个次要模式；组合冲突时会在此给出提示。",
    options: [
      { value: "SURVIVAL_TRIAL", label: "生存试炼", hint: "资源与风险压力优先" },
      { value: "SOCIAL_STRATEGY", label: "社会策略", hint: "在制度与舆论中寻找可执行空间" },
      { value: "RELATIONSHIP_STRATEGY", label: "关系策略", hint: "信任、边界与承诺是主要变量" },
      { value: "INVESTIGATION", label: "调查推理", hint: "信息获取与真伪判断优先" },
      { value: "MANAGEMENT", label: "经营治理", hint: "资源、权限与规则设计优先" },
      { value: "ETHICAL_DECISION", label: "伦理抉择", hint: "价值冲突优先，代价多为不可逆" },
    ],
  },
  {
    id: "dramatic_genre",
    index: 3,
    title: "戏剧类型",
    question: "这部作品的核心戏剧张力来自哪里？",
    help: "主类型驱动节奏模板，次类型只影响局部手法。",
    options: [
      { value: "SUSPENSE", label: "悬疑", hint: "信息不对称制造张力" },
      { value: "ROMANCE", label: "情感", hint: "关系变化制造张力" },
      { value: "COMEDY", label: "喜剧", hint: "错位与预期落空制造张力" },
      { value: "REVENGE", label: "复仇", hint: "债务清算驱动行动" },
      { value: "THRILLER", label: "惊悚", hint: "即时危险驱动行动" },
      { value: "FAMILY", label: "家庭", hint: "亲密关系中的权力再分配" },
      { value: "WORKPLACE", label: "职场", hint: "规则与晋升构成压力" },
    ],
  },
  {
    id: "subject_domain",
    index: 4,
    title: "题材域",
    question: "故事发生在什么时代、什么地方、涉及哪些专业领域？",
    help: "专业领域会生成研究关口（research_gates），未通过前不得写入正式版本。",
    options: [
      {
        value: "song_dynasty_fiction",
        label: "宋代（架空清河县）",
        hint: "制度可执行性要求高",
      },
      { value: "republican_era", label: "民国", hint: "需核实机构与称谓" },
      { value: "contemporary_urban", label: "当代都市", hint: "研究关口最少" },
      { value: "fantasy_otherworld", label: "架空/志怪", hint: "需自建制度一致性" },
    ],
  },
  {
    id: "audience_profile",
    index: 5,
    title: "受众画像",
    question: "主要面向谁？",
    help: "影响选择呈现密度与默认难度，不改变结构规则。",
    options: [
      { value: "WOMEN_18_34", label: "女性 18-34", hint: "关系与自我实现优先" },
      { value: "URBAN_WOMEN_22_34", label: "都市女性 22-34", hint: "职场与关系并重" },
      { value: "MEN_18_34", label: "男性 18-34", hint: "策略与胜负优先" },
      { value: "GENERAL_18_45", label: "泛人群 18-45", hint: "节奏优先，降低信息密度" },
      { value: "TEEN_15_20", label: "青少年 15-20", hint: "需额外合规审查" },
    ],
  },
  {
    id: "platform_profile",
    index: 6,
    title: "平台形态",
    question: "在什么平台上播出？",
    help: "决定单集时长、镜头数与素材倍数阈值。",
    options: [
      { value: "VERTICAL_VIDEO", label: "竖屏短视频", hint: "单集 ≤3 分钟" },
      {
        value: "VERTICAL_INTERACTIVE_DRAMA_8MIN",
        label: "竖屏互动剧 8 分钟",
        hint: "单集 6-9 分钟",
      },
      { value: "HORIZONTAL_LONG_EPISODE", label: "横屏长剧集", hint: "单集 ≥20 分钟" },
      { value: "MOBILE_SERIAL", label: "移动端连载", hint: "按章节切分，追更驱动" },
    ],
  },
];

/** 六维分类的维度数量，用于"未选满不得解析"的判定。 */
export const DIMENSION_COUNT = TAXONOMY.length;
